import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { getKnownAppIcon } from "@/components/shapes/connection-frame/app-icons";
import { CliLogo, type CliChoice } from "@/components/ui/cli-logo";
import { useDenkerAvatar } from "@/hooks/use-denker-avatar";
import { workToastAppsLabel, workToastRecentApps } from "@/lib/work-toast-apps";
import type { WorkToastAppRef, WorkToastItem } from "@/stores/work-toast-store";
import { ToastLogoTile } from "./toast-pill";
import { useWorkToastAppVisual } from "./use-work-toast-app-visual";

/** Shared round Denker art for toasts and compact target identities:
 *  Denker's own face, on the colour of the project when it is known. */
export function WorkToastDenkerArt({
  projectId,
  size,
}: {
  projectId?: string | null;
  size: number;
}) {
  const { seed, backgroundColor } = useDenkerAvatar(projectId);
  return (
    <AgentAvatarPreview
      styleKey="glass"
      seed={seed}
      backgroundColor={backgroundColor}
      size={size}
      sizeClassName="h-full w-full"
    />
  );
}

/** An integration logo's inset as a share of the circle (4px at 28px, 3px at 20px). */
const LOGO_INSET = 0.16;

/** A Mac app's art fills the circle and an integration's logo sits whole
 *  inside it; unresolved refs show Denker's avatar. */
export function WorkToastAppArt({
  appRef,
  projectId,
  size,
}: {
  appRef: WorkToastAppRef;
  projectId: string | null | undefined;
  size: number;
}) {
  const { label, iconUrl } = useWorkToastAppVisual(appRef);
  if (!iconUrl) {
    const BrandIcon = getKnownAppIcon(appRef.appId);
    if (BrandIcon) {
      return (
        <span
          className="grid h-full w-full place-items-center"
          data-app-art="brand"
          title={label}
        >
          <BrandIcon size={size} />
        </span>
      );
    }
    return (
      <span
        className="grid h-full w-full place-items-center"
        data-app-art="avatar"
        title={label}
      >
        <WorkToastDenkerArt projectId={projectId} size={size} />
      </span>
    );
  }
  if (appRef.route === "mcp") {
    // An integration's logo is often wide (Gmail, Search Console): it sits
    // whole inside the circle, inset from its edge, on the slot's fill.
    return (
      <img
        alt=""
        src={iconUrl}
        className="h-full w-full object-contain"
        style={{ padding: Math.round(size * LOGO_INSET) }}
        data-app-art="logo"
      />
    );
  }
  return (
    <span
      className="block h-full w-full bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${iconUrl}")`, backgroundSize: "124%" }}
      data-app-art="bundle"
    />
  );
}

/**
 * One icon for all the apps a toast stands for: the most recent app in the
 * standard tile, and the number of other apps on a badge at its corner. The
 * slot is 28px wide however many apps there are. Hover and assistive
 * technology get every app by name.
 */
export function WorkToastAppsLogo({ item }: { item: WorkToastItem }) {
  const apps = workToastRecentApps(item);
  const [primary, ...others] = apps;
  if (!primary) return null;
  const label = workToastAppsLabel(apps);
  return (
    <span
      className="relative shrink-0"
      role="img"
      aria-label={label}
      title={label}
      data-testid="work-toast-logo-app"
    >
      {/* The tile ignores the pointer so the hover reads this wrapper's title. */}
      <ToastLogoTile className="pointer-events-none">
        <WorkToastAppArt
          appRef={primary}
          projectId={item.projectId}
          size={28}
        />
      </ToastLogoTile>
      {others.length > 0 && (
        // Opaque on its own (the surface under the tile's soft fill), so it
        // reads over the icon whatever the panel behind it is made of.
        <span
          className="pointer-events-none absolute -bottom-[3px] -right-1.5 grid h-[15px] min-w-[15px] place-items-center rounded-pill px-[3px] text-appkit-mini font-semibold leading-none text-primary ring-[1.5px] ring-surface [background:linear-gradient(var(--color-surface-selected),var(--color-surface-selected)),var(--color-surface)]"
          aria-hidden="true"
          data-testid="work-toast-app-count"
        >
          +{others.length}
        </span>
      )}
    </span>
  );
}

export function WorkToastProviderLogo({ provider }: { provider: CliChoice }) {
  const sizeClassName = provider === "codex" ? "h-7 w-7" : "h-5 w-5";
  return (
    <ToastLogoTile testId="work-toast-logo-provider">
      <CliLogo cli={provider} className={sizeClassName} />
    </ToastLogoTile>
  );
}

export function WorkToastDenkerLogo({
  projectId,
}: {
  projectId: string | null;
}) {
  return (
    <ToastLogoTile testId="work-toast-logo-denker">
      <WorkToastDenkerArt projectId={projectId} size={28} />
    </ToastLogoTile>
  );
}
